import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Card,
  ChevronDownIcon,
  CountChip,
  DateInput,
  GripVerticalIcon,
  Hint,
  Label,
  NumberInput,
  PlusIcon,
  SegmentedControl,
  Select,
  Switch,
  TextInput,
  XIcon,
} from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { pivotReport } from './reportPivot';
import ReportPivotResult from './ReportPivotResult';
import {
  BUCKET_OPTIONS,
  applyAddSource,
  applyMovePlaced,
  applyRemovePlaced,
  createCatalogLookups,
  dimLabel,
  fieldToken,
  filterLabel,
  flattenFields,
  formatFilterSummary,
  isBucketable,
  measureFnLabel,
  measureFnsFor,
  measureLabel,
  parseToken,
  type PlacedDim,
  type PlacedFilter,
  type PlacedMeasure,
  type ReportPivotDraft,
  type Zone,
} from './reportPivotModel';
import type {
  EFormReportCatalog,
  EFormReportChartType,
  EFormReportQueryResult,
} from './useData';

const collisionDetection: CollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  return pointerHits.length ? pointerHits : closestCenter(args);
};

function FieldChip({
  label,
  hint,
  dragging,
}: {
  label: string;
  hint?: string;
  dragging?: boolean;
}) {
  return (
    <div
      className={cn(
        'group flex w-full items-center gap-1.5 rounded-md border px-2 py-1 text-left text-xs transition-all',
        'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40',
        'dark:border-slate-700 dark:bg-slate-900 dark:hover:border-blue-700 dark:hover:bg-blue-950/20',
        dragging && 'shadow-lg ring-2 ring-blue-500',
      )}
    >
      <GripVerticalIcon className="h-3.5 w-3.5 shrink-0 text-slate-300 transition-colors group-hover:text-blue-500" />
      <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
      {hint ? (
        <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[9px] uppercase tracking-wide text-slate-500 dark:bg-slate-800">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function DraggableSource({
  id,
  label,
  hint,
  onAdd,
}: {
  id: string;
  label: string;
  hint?: string;
  onAdd?: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  return (
    <div ref={setNodeRef} className={cn('flex w-full items-center gap-1', isDragging && 'opacity-40')}>
      <Hint
        content="Drag to Filters, Numbers, or Pivot across"
        side="right"
        triggerClassName="min-w-0 flex-1"
      >
        <button
          type="button"
          className="w-full cursor-grab active:cursor-grabbing touch-none"
          {...listeners}
          {...attributes}
          aria-label={`Drag ${label}`}
        >
          <FieldChip label={label} hint={hint} />
        </button>
      </Hint>
      {onAdd ? (
        <button
          type="button"
          className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
          title={`Add ${label} as a table column`}
          aria-label={`Add ${label} as a table column`}
          onClick={onAdd}
        >
          <PlusIcon className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

function FieldsTrash() {
  const { setNodeRef, isOver } = useDroppable({ id: 'zone:fields' });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex min-h-7 w-full items-center justify-center rounded-md border border-dashed px-2 py-1 text-center text-[10px] transition-all',
        isOver
          ? 'border-red-400 bg-red-50 font-medium text-red-600 dark:bg-red-950/30'
          : 'border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-950/30',
      )}
    >
      {isOver ? 'Release to remove' : 'Drop here to remove'}
    </div>
  );
}

function SortableItem({
  id,
  children,
}: {
  id: string;
  children: (handle: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
  }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && 'opacity-40')}
    >
      {children({ attributes, listeners })}
    </div>
  );
}

function ChipPanel({
  open,
  onClose,
  anchorRef,
  children,
  width = 280,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  width?: number;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) {
      setRect(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      const top = Math.min(r.bottom + 6, window.innerHeight - 12);
      setRect({ top, left });
    };
    update();
    const onScrollOrResize = () => update();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, anchorRef, width]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = anchorRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inTrigger && !inPanel) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [open, onClose, anchorRef]);

  if (!open || !rect) return null;
  return createPortal(
    <div
      ref={panelRef}
      className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-900"
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width,
        ...zStyle(Z_INDEX.portaledDropdown),
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

function AxisStrip({
  id,
  title,
  emptyHint,
  itemIds,
  orientation,
  compact,
  children,
}: {
  id: string;
  title: string;
  emptyHint: string;
  itemIds: string[];
  orientation: 'horizontal' | 'vertical';
  /** Single-line rail row: label left, chips right (no stacked card chrome). */
  compact?: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const strategy =
    orientation === 'horizontal' ? horizontalListSortingStrategy : verticalListSortingStrategy;

  if (compact) {
    return (
      <div
        ref={setNodeRef}
        className={cn(
          'flex min-h-9 items-start gap-2 rounded-lg px-2 py-1.5 transition-all',
          isOver
            ? 'bg-blue-50 ring-1 ring-inset ring-blue-500 dark:bg-blue-950/40'
            : 'hover:bg-slate-50 dark:hover:bg-slate-800/40',
        )}
      >
        <div className="flex w-20 shrink-0 items-center gap-1 pt-0.5">
          <Label className="!mb-0 text-[10px] uppercase tracking-wider">{title}</Label>
          {itemIds.length ? <CountChip>{itemIds.length}</CountChip> : null}
        </div>
        <SortableContext items={itemIds} strategy={strategy}>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {children}
            {!itemIds.length ? (
              <span className="text-[11px] text-slate-400">{emptyHint}</span>
            ) : null}
          </div>
        </SortableContext>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-xl border border-dashed p-2 transition-all',
        isOver
          ? 'border-blue-500 bg-blue-50/80 shadow-inner dark:bg-blue-950/40'
          : 'border-slate-300 bg-slate-50/50 dark:border-slate-600 dark:bg-slate-950/20',
        orientation === 'vertical' && 'min-h-[160px] w-full max-w-[220px]',
        orientation === 'horizontal' && 'min-h-[52px]',
      )}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 px-0.5">
        <Label className="!mb-0 text-[10px] uppercase tracking-wider">{title}</Label>
        <CountChip>{itemIds.length}</CountChip>
      </div>
      <SortableContext items={itemIds} strategy={strategy}>
        <div
          className={cn(
            orientation === 'horizontal' ? 'flex flex-wrap items-center gap-1.5' : 'space-y-1.5',
          )}
        >
          {children}
          {!itemIds.length ? (
            <div
              className={cn(
                'rounded-md border border-dashed border-slate-200 px-2 py-1.5 text-center text-[11px] text-slate-400 dark:border-slate-700',
                orientation === 'horizontal' ? 'min-w-[120px]' : 'w-full',
              )}
            >
              {emptyHint}
            </div>
          ) : null}
        </div>
      </SortableContext>
    </div>
  );
}

function CompactChipShell({
  children,
  onRemove,
  handle,
  fullWidth,
}: {
  children: React.ReactNode;
  onRemove: () => void;
  handle: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
  };
  fullWidth?: boolean;
}) {
  return (
    <div
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs shadow-sm dark:border-slate-700 dark:bg-slate-900',
        fullWidth && 'w-full',
      )}
    >
      <button
        type="button"
        className="shrink-0 cursor-grab touch-none text-slate-400 active:cursor-grabbing"
        {...handle.listeners}
        {...handle.attributes}
        aria-label="Drag"
      >
        <GripVerticalIcon className="h-3.5 w-3.5" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        onClick={onRemove}
      >
        <XIcon className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export type ReportPivotBuilderProps = {
  catalog: EFormReportCatalog;
  state: ReportPivotDraft;
  onChange: React.Dispatch<React.SetStateAction<ReportPivotDraft>>;
  result: EFormReportQueryResult | null;
  running?: boolean;
  extraToolbar?: React.ReactNode;
  sidebarExtra?: React.ReactNode;
  onCellClick?: (row: Record<string, unknown>, colEquals?: Record<string, unknown>) => void;
};

const ReportPivotBuilder: React.FC<ReportPivotBuilderProps> = ({
  catalog,
  state,
  onChange,
  result,
  running,
  extraToolbar,
  sidebarExtra,
  onCellClick,
}) => {
  const { colors } = useTheme();
  const [fieldFilter, setFieldFilter] = useState('');
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [openChipId, setOpenChipId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const lookups = useMemo(() => createCatalogLookups(catalog), [catalog]);
  const allFields = useMemo(() => flattenFields(catalog.fields ?? []), [catalog.fields]);

  const display = useMemo(
    () => (result ? pivotReport(result, { showTotals: state.showTotals }) : null),
    [result, state.showTotals],
  );

  const filteredFields = useMemo(() => {
    const q = fieldFilter.trim().toLowerCase();
    if (!q) return allFields;
    return allFields.filter(
      (f) =>
        f.label.toLowerCase().includes(q) ||
        f.definitionName?.toLowerCase().includes(q) ||
        f.relation?.name.toLowerCase().includes(q) ||
        f.fieldKey.toLowerCase().includes(q) ||
        f.type.toLowerCase().includes(q),
    );
  }, [allFields, fieldFilter]);

  const filteredMeta = useMemo(() => {
    const q = fieldFilter.trim().toLowerCase();
    return (catalog.meta || []).filter(
      (m) =>
        m.role !== 'none' &&
        (!q || m.label.toLowerCase().includes(q) || m.metaKey.toLowerCase().includes(q)),
    );
  }, [catalog.meta, fieldFilter]);

  const patch = useCallback(
    (updater: (prev: ReportPivotDraft) => ReportPivotDraft) => {
      onChange(updater);
    },
    [onChange],
  );

  const indexInZone = (zone: Zone, itemUid: string) => {
    const list =
      zone === 'rows'
        ? state.rows
        : zone === 'columns'
          ? state.columns
          : zone === 'values'
            ? state.values
            : state.filters;
    return list.findIndex((item) => item.uid === itemUid);
  };

  const onDragStart = (event: DragStartEvent) => {
    setOpenChipId(null);
    setActiveDragId(String(event.active.id));
  };
  const onDragCancel = () => setActiveDragId(null);

  const onDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const active = parseToken(String(event.active.id));
    const over = event.over?.id ? parseToken(String(event.over.id)) : null;
    if (!active || !over) return;
    const overZone: Zone | 'fields' | null =
      over.type === 'zone' ? over.zone : over.type === 'placed' ? over.zone : null;
    const overIndex = over.type === 'placed' ? indexInZone(over.zone, over.uid) : undefined;
    if (!overZone) return;
    if (overZone === 'fields') {
      if (active.type === 'placed') {
        patch((prev) => applyRemovePlaced(prev, active.zone, active.uid));
      }
      return;
    }
    const zone: Zone = overZone;
    if (active.type === 'source') {
      patch((prev) => applyAddSource(prev, active.kind, active.key, zone, lookups, overIndex));
      return;
    }
    if (active.type !== 'placed') return;
    patch((prev) =>
      applyMovePlaced(prev, active.zone, active.uid, zone, lookups, overIndex),
    );
  };

  const overlayLabel = (() => {
    if (!activeDragId) return '';
    const token = parseToken(activeDragId);
    if (!token) return '';
    if (token.type === 'source') {
      return token.kind === 'meta'
        ? lookups.findMeta(token.key)?.label || token.key
        : lookups.findFieldToken(token.key)?.label || token.key;
    }
    if (token.type === 'placed') {
      if (token.zone === 'values') {
        return measureLabel(
          state.values.find((v) => v.uid === token.uid) || { fn: 'count' },
          lookups,
        );
      }
      if (token.zone === 'filters') {
        return filterLabel(
          state.filters.find((f) => f.uid === token.uid) || { op: 'eq' },
          lookups,
        );
      }
      const dim = [...state.rows, ...state.columns].find((d) => d.uid === token.uid);
      return dim ? dimLabel(dim, lookups) : '';
    }
    return '';
  })();

  const rowIds = state.rows.map((d) => `row:${d.uid}`);
  const colIds = state.columns.map((d) => `col:${d.uid}`);
  const valIds = state.values.map((d) => `val:${d.uid}`);
  const fltIds = state.filters.map((d) => `flt:${d.uid}`);

  const updateDim = (zone: 'rows' | 'columns', itemUid: string, next: Partial<PlacedDim>) => {
    patch((prev) => ({
      ...prev,
      [zone]: prev[zone].map((d) => (d.uid === itemUid ? { ...d, ...next } : d)),
    }));
  };

  const updateMeasure = (itemUid: string, next: Partial<PlacedMeasure>) => {
    patch((prev) => ({
      ...prev,
      values: prev.values.map((m) => (m.uid === itemUid ? { ...m, ...next } : m)),
    }));
  };

  const updateFilter = (itemUid: string, next: Partial<PlacedFilter>) => {
    patch((prev) => ({
      ...prev,
      filters: prev.filters.map((f) => (f.uid === itemUid ? { ...f, ...next } : f)),
    }));
  };

  const renderDimChip = (d: PlacedDim, zone: 'rows' | 'columns') => {
    const chipId = `${zone}:${d.uid}`;
    const bucketable = isBucketable(d, lookups);
    return (
      <SortableItem key={d.uid} id={`${zone === 'rows' ? 'row' : 'col'}:${d.uid}`}>
        {({ attributes, listeners }) => (
          <DimChip
            d={d}
            label={dimLabel(d, lookups)}
            bucketable={bucketable}
            open={openChipId === chipId}
            onOpenChange={(open) => setOpenChipId(open ? chipId : null)}
            onRemove={() => patch((prev) => applyRemovePlaced(prev, zone, d.uid))}
            onBucket={(bucket) => updateDim(zone, d.uid, { bucket })}
            handle={{ attributes, listeners }}
            fullWidth={zone === 'rows'}
          />
        )}
      </SortableItem>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div className="grid h-full min-h-0 grid-cols-1 gap-3 xl:grid-cols-12">
        <aside className="flex min-h-0 flex-col xl:col-span-3 2xl:col-span-2">
          <Card className="flex h-full min-h-0 w-full flex-col space-y-2 p-2">
            <div className="flex shrink-0 items-center justify-between gap-2">
              <Label className="!mb-0 text-sm">Available fields</Label>
              <CountChip>{filteredMeta.length + filteredFields.length}</CountChip>
            </div>
            <p className="text-[11px] leading-snug text-slate-500">
              Click a field to add it as a table column.
            </p>
            <TextInput
              value={fieldFilter}
              onChange={(e) => setFieldFilter(e.target.value)}
              placeholder="Search…"
              className="w-full shrink-0"
            />
            <div className="min-h-0 w-full flex-1 space-y-1.5 overflow-y-auto pr-0.5">
              <FieldsTrash />
              {filteredMeta.length ? (
                <div className="w-full space-y-0.5">
                  <p className="px-0.5 text-[9px] font-semibold uppercase tracking-widest text-slate-400">
                    Submission
                  </p>
                  {filteredMeta.map((m) => (
                    <DraggableSource
                      key={`src:meta:${m.metaKey}`}
                      id={`src:meta:${m.metaKey}`}
                      label={m.label}
                      hint={m.dataType}
                      onAdd={() =>
                        patch((prev) => applyAddSource(prev, 'meta', m.metaKey, 'rows', lookups))
                      }
                    />
                  ))}
                </div>
              ) : null}
              {filteredFields.length ? (
                <div className="w-full space-y-0.5">
                  <p className="px-0.5 text-[9px] font-semibold uppercase tracking-widest text-slate-400">
                    Form fields
                  </p>
                  {filteredFields.map((f) => (
                    <DraggableSource
                      key={`src:field:${fieldToken(f)}`}
                      id={`src:field:${fieldToken(f)}`}
                      label={f.label}
                      hint={f.dataType || f.type}
                      onAdd={() =>
                        patch((prev) => applyAddSource(prev, 'field', fieldToken(f), 'rows', lookups))
                      }
                    />
                  ))}
                </div>
              ) : null}
              {!filteredMeta.length && !filteredFields.length ? (
                <p className="py-6 text-center text-xs text-slate-400">No fields match your search.</p>
              ) : null}
            </div>
          </Card>
          {sidebarExtra}
        </aside>

        <section className="min-h-0 space-y-3 overflow-y-auto pb-2 xl:col-span-9 2xl:col-span-10">
          <Card className="divide-y divide-slate-200 overflow-hidden p-0 dark:divide-slate-700">
            <AxisStrip
              id="zone:filters"
              title="Filters"
              emptyHint="Drop fields to filter"
              itemIds={fltIds}
              orientation="horizontal"
              compact
            >
              {state.filters.map((f) => (
                <SortableItem key={f.uid} id={`flt:${f.uid}`}>
                  {({ attributes, listeners }) => (
                    <FilterChip
                      filter={f}
                      label={filterLabel(f, lookups)}
                      field={lookups.findField(f.fieldId, f.relationId)}
                      meta={lookups.findMeta(f.metaKey)}
                      open={openChipId === `flt:${f.uid}`}
                      onOpenChange={(open) => setOpenChipId(open ? `flt:${f.uid}` : null)}
                      onChange={(next) => updateFilter(f.uid, next)}
                      onRemove={() => patch((prev) => applyRemovePlaced(prev, 'filters', f.uid))}
                      handle={{ attributes, listeners }}
                    />
                  )}
                </SortableItem>
              ))}
            </AxisStrip>

            <AxisStrip
              id="zone:columns"
              title={state.chartType === 'table' ? 'Pivot across (optional)' : 'Columns'}
              emptyHint={
                state.chartType === 'table'
                  ? 'Leave empty for a simple table'
                  : 'Drop a field to pivot across columns'
              }
              itemIds={colIds}
              orientation="horizontal"
              compact
            >
              {state.columns.map((d) => renderDimChip(d, 'columns'))}
            </AxisStrip>

            <AxisStrip
              id="zone:values"
              title="Numbers"
              emptyHint="Optional: Count, Sum, Average…"
              itemIds={valIds}
              orientation="horizontal"
              compact
            >
              {state.values.map((m) => {
                const field = lookups.findField(m.fieldId, m.relationId);
                const fnOptions = measureFnsFor(field?.dataType, m.metaKey);
                return (
                  <SortableItem key={m.uid} id={`val:${m.uid}`}>
                    {({ attributes, listeners }) => (
                      <MeasureChip
                        measure={m}
                        label={measureLabel(m, lookups)}
                        dataType={field?.dataType}
                        fnOptions={fnOptions}
                        open={openChipId === `val:${m.uid}`}
                        onOpenChange={(open) => setOpenChipId(open ? `val:${m.uid}` : null)}
                        onFn={(fn) => {
                          const prefix = fn === 'pct_true' ? 'pct' : fn;
                          updateMeasure(m.uid, {
                            fn,
                            alias: `${prefix}_${field?.fieldKey || m.metaKey || m.fieldKey || 'value'}`,
                          });
                        }}
                        onRemove={() => patch((prev) => applyRemovePlaced(prev, 'values', m.uid))}
                        handle={{ attributes, listeners }}
                      />
                    )}
                  </SortableItem>
                );
              })}
            </AxisStrip>

            <div className="flex flex-wrap items-center gap-3 px-3 py-2">
              {extraToolbar}
              <Switch
                size="sm"
                checked={state.showTotals}
                onChange={(e) => patch((prev) => ({ ...prev, showTotals: e.target.checked }))}
                label="Totals"
              />
              <SegmentedControl
                size="sm"
                value={state.chartType}
                onChange={(v) =>
                  patch((prev) => ({ ...prev, chartType: v as EFormReportChartType }))
                }
                options={[
                  { id: 'table', label: 'Table' },
                  { id: 'bar', label: 'Bar' },
                  { id: 'line', label: 'Line' },
                  { id: 'pie', label: 'Pie' },
                ]}
                aria-label="Chart type"
              />
              <CountChip>
                {display?.rows.filter((r) => !r.__total).length ?? 0} rows
              </CountChip>
            </div>
          </Card>

          <ReportPivotResult
            result={result}
            chartType={state.chartType}
            showTotals={state.showTotals}
            running={running}
            showRowCount={false}
            emptyHint="Click fields on the left to add table columns. The table appears here."
            onCellClick={onCellClick}
            leadingSlot={
              <div className="shrink-0 md:w-44">
                <AxisStrip
                  id="zone:rows"
                  title={state.chartType === 'table' ? 'Table columns' : 'Rows'}
                  emptyHint={state.chartType === 'table' ? 'Click fields to add columns' : 'Drop row fields'}
                  itemIds={rowIds}
                  orientation="vertical"
                >
                  {state.rows.map((d) => renderDimChip(d, 'rows'))}
                </AxisStrip>
              </div>
            }
          />
        </section>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDragId ? (
          <div className="pointer-events-none w-56">
            <FieldChip label={overlayLabel || 'Field'} dragging />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

function DimChip({
  d,
  label,
  bucketable,
  open,
  onOpenChange,
  onRemove,
  onBucket,
  handle,
  fullWidth,
}: {
  d: PlacedDim;
  label: string;
  bucketable: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemove: () => void;
  onBucket: (bucket: PlacedDim['bucket']) => void;
  handle: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
  };
  fullWidth?: boolean;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bucketLabel =
    BUCKET_OPTIONS.find((o) => o.value === (d.bucket || 'month'))?.label || d.bucket;
  return (
    <CompactChipShell onRemove={onRemove} handle={handle} fullWidth={fullWidth}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate font-medium leading-tight">{label}</span>
        {bucketable ? (
          <>
            <button
              ref={triggerRef}
              type="button"
              className={cn(
                'inline-flex w-fit max-w-full items-center gap-1 rounded border px-1.5 py-0.5 text-left text-[10px] font-semibold uppercase tracking-wide transition-colors',
                'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700',
                'dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-blue-600 dark:hover:bg-blue-950/40 dark:hover:text-blue-300',
              )}
              onClick={() => onOpenChange(!open)}
              aria-label={`Date bucket: ${bucketLabel}`}
            >
              {bucketLabel}
              <ChevronDownIcon className="h-3 w-3 opacity-60" />
            </button>
            <ChipPanel open={open} onClose={() => onOpenChange(false)} anchorRef={triggerRef} width={200}>
              <Label className="!mb-1 text-xs">Date bucket</Label>
              <Select
                size="sm"
                value={d.bucket || 'month'}
                onChange={(e) => {
                  onBucket(e.target.value as PlacedDim['bucket']);
                  onOpenChange(false);
                }}
                options={BUCKET_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              />
            </ChipPanel>
          </>
        ) : null}
      </div>
    </CompactChipShell>
  );
}

function MeasureChip({
  measure,
  label,
  dataType,
  fnOptions,
  open,
  onOpenChange,
  onFn,
  onRemove,
  handle,
}: {
  measure: PlacedMeasure;
  label: string;
  dataType?: string;
  fnOptions: ReturnType<typeof measureFnsFor>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFn: (fn: PlacedMeasure['fn']) => void;
  onRemove: () => void;
  handle: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
  };
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const fnText = measureFnLabel(measure.fn, dataType, measure.metaKey);
  return (
    <CompactChipShell onRemove={onRemove} handle={handle}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 truncate font-medium leading-tight">{label}</span>
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            'inline-flex shrink-0 items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] font-semibold tracking-wide transition-colors',
            'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100',
            'dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:border-blue-600',
            fnOptions.length <= 1 && 'cursor-default',
          )}
          onClick={() => fnOptions.length > 1 && onOpenChange(!open)}
          aria-label={`Aggregation: ${fnText}`}
        >
          {fnText}
          {fnOptions.length > 1 ? <ChevronDownIcon className="h-3 w-3 opacity-60" /> : null}
        </button>
      </div>
      {fnOptions.length > 1 ? (
        <ChipPanel open={open} onClose={() => onOpenChange(false)} anchorRef={triggerRef} width={220}>
          <Label className="!mb-1 text-xs">Aggregation</Label>
          <Select
            size="sm"
            value={measure.fn}
            onChange={(e) => {
              onFn(e.target.value as PlacedMeasure['fn']);
              onOpenChange(false);
            }}
            options={fnOptions.map((fn) => ({
              value: fn,
              label: measureFnLabel(fn, dataType, measure.metaKey),
            }))}
          />
        </ChipPanel>
      ) : null}
    </CompactChipShell>
  );
}

function FilterChip({
  filter,
  label,
  field,
  meta,
  open,
  onOpenChange,
  onChange,
  onRemove,
  handle,
}: {
  filter: PlacedFilter;
  label: string;
  field?: { dataType?: string; options?: Array<{ value: string; label: string }> };
  meta?: { dataType?: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (next: Partial<PlacedFilter>) => void;
  onRemove: () => void;
  handle: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
  };
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dataType = field?.dataType || meta?.dataType || 'text';
  const opOptions =
    dataType === 'date'
      ? [
          { value: 'between', label: 'Between' },
          { value: 'gte', label: 'On/after' },
          { value: 'lte', label: 'On/before' },
        ]
      : dataType === 'number'
        ? [
            { value: 'eq', label: '=' },
            { value: 'gte', label: '≥' },
            { value: 'lte', label: '≤' },
          ]
        : dataType === 'enum' || dataType === 'bool'
          ? [{ value: 'eq', label: 'Equals' }]
          : [
              { value: 'contains', label: 'Contains' },
              { value: 'eq', label: 'Equals' },
            ];

  return (
    <CompactChipShell onRemove={onRemove} handle={handle}>
      <button
        ref={triggerRef}
        type="button"
        className="flex max-w-[240px] items-center gap-1 truncate text-left font-medium"
        onClick={() => onOpenChange(!open)}
      >
        <span className="truncate">{label}</span>
        <span className="truncate text-[10px] font-normal text-slate-500">
          {formatFilterSummary(filter)}
        </span>
      </button>
      <ChipPanel open={open} onClose={() => onOpenChange(false)} anchorRef={triggerRef} width={320}>
        <div className="space-y-2">
          <Label className="!mb-0 text-xs">{label}</Label>
          <div className="flex flex-wrap gap-2">
            <div className="w-28">
              <Select
                size="sm"
                value={filter.op}
                onChange={(e) => onChange({ op: e.target.value })}
                options={opOptions}
              />
            </div>
            {dataType === 'date' ? (
              <>
                <div className="w-36">
                  <DateInput
                    value={String(filter.value || '')}
                    onChange={(e) => onChange({ value: e.target.value })}
                  />
                </div>
                {filter.op === 'between' ? (
                  <div className="w-36">
                    <DateInput
                      value={String(filter.valueTo || '')}
                      onChange={(e) => onChange({ valueTo: e.target.value })}
                    />
                  </div>
                ) : null}
              </>
            ) : dataType === 'number' ? (
              <div className="w-28">
                <NumberInput
                  size="sm"
                  value={filter.value == null ? '' : Number(filter.value)}
                  onChange={(e) => onChange({ value: e.target.value })}
                />
              </div>
            ) : dataType === 'bool' ? (
              <div className="w-28">
                <Select
                  size="sm"
                  value={String(filter.value ?? 'true')}
                  onChange={(e) => onChange({ value: e.target.value === 'true' })}
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                />
              </div>
            ) : field?.options?.length ? (
              <div className="w-40">
                <Select
                  size="sm"
                  value={String(filter.value ?? '')}
                  onChange={(e) => onChange({ value: e.target.value })}
                  options={field.options.map((o) => ({ value: o.value, label: o.label }))}
                />
              </div>
            ) : (
              <div className="w-40">
                <TextInput
                  value={String(filter.value ?? '')}
                  onChange={(e) => onChange({ value: e.target.value })}
                  placeholder="Value"
                />
              </div>
            )}
          </div>
        </div>
      </ChipPanel>
    </CompactChipShell>
  );
}

export default ReportPivotBuilder;
